// PFMEA AI Assistant - Suggested Prompts
// Specialized prompts for Process FMEA and Control Plan analysis
// 6 Categories with 42 total prompts aligned to AIAG-VDA PFMEA methodology
// ============================================================

import React from 'react';
import {
    Activity,
    AlertTriangle,
    BarChart3,
    CheckCircle,
    ClipboardList,
    FileText,
    Shield,
    Target,
    TestTube2,
    TrendingUp,
    Workflow,
    Zap,
    Settings,
    Users,
    Wrench,
    Package,
    Layers,
    Search,
    Factory,
    RefreshCw,
} from 'lucide-react';

export interface PfmeaSuggestedPrompt {
    id: string;
    category: PfmeaCategoryType;
    icon: React.ReactNode;
    title: string;
    prompt: string;
    description: string;
}

export type PfmeaCategoryType =
    | 'Process Analysis'
    | 'Failure Mode Discovery'
    | 'Risk Assessment'
    | 'Control Planning'
    | '4M Analysis'
    | 'AIAG-VDA Compliance';

export const PFMEA_CATEGORIES = [
    {
        id: 'Process Analysis' as PfmeaCategoryType,
        icon: React.createElement(Workflow, { className: "text-blue-500" }),
        color: 'bg-blue-50 border-blue-200 text-blue-700 hover:bg-blue-100',
        activeColor: 'bg-blue-500 text-white',
        description: 'Process flow and structure analysis'
    },
    {
        id: 'Failure Mode Discovery' as PfmeaCategoryType,
        icon: React.createElement(AlertTriangle, { className: "text-red-500" }),
        color: 'bg-red-50 border-red-200 text-red-700 hover:bg-red-100',
        activeColor: 'bg-red-500 text-white',
        description: 'Identify potential failure modes'
    },
    {
        id: 'Risk Assessment' as PfmeaCategoryType,
        icon: React.createElement(BarChart3, { className: "text-amber-500" }),
        color: 'bg-amber-50 border-amber-200 text-amber-700 hover:bg-amber-100',
        activeColor: 'bg-amber-500 text-white',
        description: 'S-O-D ratings and Action Priority'
    },
    {
        id: 'Control Planning' as PfmeaCategoryType,
        icon: React.createElement(ClipboardList, { className: "text-green-500" }),
        color: 'bg-green-50 border-green-200 text-green-700 hover:bg-green-100',
        activeColor: 'bg-green-500 text-white',
        description: 'Prevention and detection controls'
    },
    {
        id: '4M Analysis' as PfmeaCategoryType,
        icon: React.createElement(Settings, { className: "text-purple-500" }),
        color: 'bg-purple-50 border-purple-200 text-purple-700 hover:bg-purple-100',
        activeColor: 'bg-purple-500 text-white',
        description: 'Man, Machine, Material, Method'
    },
    {
        id: 'AIAG-VDA Compliance' as PfmeaCategoryType,
        icon: React.createElement(Shield, { className: "text-indigo-500" }),
        color: 'bg-indigo-50 border-indigo-200 text-indigo-700 hover:bg-indigo-100',
        activeColor: 'bg-indigo-500 text-white',
        description: '7-Step methodology compliance'
    },
];

export const getPfmeaSuggestedPrompts = (): PfmeaSuggestedPrompt[] => {
    // ========== PROCESS ANALYSIS (7 prompts) ==========
    const processAnalysisPrompts: PfmeaSuggestedPrompt[] = [
        {
            id: 'proc-1',
            category: 'Process Analysis',
            icon: React.createElement(Workflow, { size: 16 }),
            title: 'Process Flow Overview',
            prompt: 'Analyze the current process flow and provide an overview of all operations, inspections, and material handling steps. Identify the sequence and dependencies between process steps.',
            description: 'Map complete process structure'
        },
        {
            id: 'proc-2',
            category: 'Process Analysis',
            icon: React.createElement(Factory, { size: 16 }),
            title: 'Value Stream Analysis',
            prompt: 'Identify value-adding vs non-value-adding operations in the process. Show cycle times, identify bottlenecks, and highlight steps that could introduce quality risks.',
            description: 'Find wastes and bottlenecks'
        },
        {
            id: 'proc-3',
            category: 'Process Analysis',
            icon: React.createElement(Target, { size: 16 }),
            title: 'Critical Process Parameters',
            prompt: 'Identify the Critical Process Parameters (CPPs) for each operation. Show which parameters affect product quality and what their control ranges should be.',
            description: 'Key parameters to control'
        },
        {
            id: 'proc-4',
            category: 'Process Analysis',
            icon: React.createElement(Layers, { size: 16 }),
            title: 'Process Step Requirements',
            prompt: 'For each process step, list the key requirements that must be met. Include dimensional tolerances, surface finishes, cleanliness levels, and functional specifications.',
            description: 'Requirements per operation'
        },
        {
            id: 'proc-5',
            category: 'Process Analysis',
            icon: React.createElement(Search, { size: 16 }),
            title: 'Inspection Point Review',
            prompt: 'Analyze current inspection points in the process. Are critical characteristics being checked? Recommend additional inspection stations for high-risk operations.',
            description: 'Evaluate inspection strategy'
        },
        {
            id: 'proc-6',
            category: 'Process Analysis',
            icon: React.createElement(RefreshCw, { size: 16 }),
            title: 'Rework Loop Analysis',
            prompt: 'Identify rework operations in the process flow. For each rework station, show the source defects, rework rate, and potential for introducing new failures.',
            description: 'Analyze rework paths'
        },
        {
            id: 'proc-7',
            category: 'Process Analysis',
            icon: React.createElement(FileText, { size: 16 }),
            title: 'PFMEA Coverage Check',
            prompt: 'Compare process flow steps against PFMEA entries. Identify any process operations that are missing from the FMEA analysis.',
            description: 'Ensure complete coverage'
        },
    ];

    // ========== FAILURE MODE DISCOVERY (7 prompts) ==========
    const failureModePrompts: PfmeaSuggestedPrompt[] = [
        {
            id: 'fm-1',
            category: 'Failure Mode Discovery',
            icon: React.createElement(AlertTriangle, { size: 16 }),
            title: 'Generate Failure Modes',
            prompt: 'For each process step, generate potential failure modes. Consider: incomplete action, wrong action, excessive action, insufficient action, wrong sequence, and wrong timing.',
            description: 'AI-powered failure brainstorm'
        },
        {
            id: 'fm-2',
            category: 'Failure Mode Discovery',
            icon: React.createElement(Zap, { size: 16 }),
            title: 'High-Severity Failure Scan',
            prompt: 'Identify failure modes that could affect product safety or regulatory compliance. Rate potential severity impacts on the end customer.',
            description: 'Safety-critical failures'
        },
        {
            id: 'fm-3',
            category: 'Failure Mode Discovery',
            icon: React.createElement(Activity, { size: 16 }),
            title: 'Frequent Failure Patterns',
            prompt: 'Based on historical data and industry benchmarks, identify failure modes most likely to occur. Focus on common manufacturing defects for this process type.',
            description: 'High-occurrence failures'
        },
        {
            id: 'fm-4',
            category: 'Failure Mode Discovery',
            icon: React.createElement(Package, { size: 16 }),
            title: 'Material-Related Failures',
            prompt: 'Analyze potential failures related to incoming materials. Consider out-of-spec materials, contamination, wrong material, and lot-to-lot variation.',
            description: 'Material handling risks'
        },
        {
            id: 'fm-5',
            category: 'Failure Mode Discovery',
            icon: React.createElement(Settings, { size: 16 }),
            title: 'Equipment-Related Failures',
            prompt: 'Identify failure modes caused by equipment issues. Consider calibration drift, tool wear, machine breakdown, and maintenance-induced failures.',
            description: 'Machine and tooling risks'
        },
        {
            id: 'fm-6',
            category: 'Failure Mode Discovery',
            icon: React.createElement(Users, { size: 16 }),
            title: 'Operator-Related Failures',
            prompt: 'Identify human-factor failure modes. Consider training gaps, fatigue, distraction, incorrect tool usage, and procedural non-compliance.',
            description: 'Human error risks'
        },
        {
            id: 'fm-7',
            category: 'Failure Mode Discovery',
            icon: React.createElement(TrendingUp, { size: 16 }),
            title: 'Failure Effect Chain',
            prompt: 'For top failure modes, trace the effects chain: Local effect → Next operation → End customer. Ensure all three levels are documented.',
            description: 'Effect propagation analysis'
        },
    ];

    // ========== RISK ASSESSMENT (7 prompts) ==========
    const riskAssessmentPrompts: PfmeaSuggestedPrompt[] = [
        {
            id: 'risk-1',
            category: 'Risk Assessment',
            icon: React.createElement(BarChart3, { size: 16 }),
            title: 'Action Priority Summary',
            prompt: 'List all PFMEA items by Action Priority (AP). Show counts of High, Medium, and Low priority items. Highlight which operations have the most High-AP items.',
            description: 'Overall AP distribution'
        },
        {
            id: 'risk-2',
            category: 'Risk Assessment',
            icon: React.createElement(AlertTriangle, { size: 16 }),
            title: 'Top 10 High-Risk Items',
            prompt: 'List the top 10 highest-risk failure modes. For each show: Process Step, Failure Mode, S-O-D ratings, AP level, current controls, and recommended actions.',
            description: 'Priority improvement list'
        },
        {
            id: 'risk-3',
            category: 'Risk Assessment',
            icon: React.createElement(Zap, { size: 16 }),
            title: 'Severity 8+ Analysis',
            prompt: 'Extract all items with Severity ≥ 8. These require mandatory actions per AIAG-VDA. Show current mitigation status and recommended design changes.',
            description: 'Safety-critical items'
        },
        {
            id: 'risk-4',
            category: 'Risk Assessment',
            icon: React.createElement(Activity, { size: 16 }),
            title: 'Occurrence Reduction Opportunities',
            prompt: 'Identify items where Occurrence is the primary risk driver (O ≥ 6). Recommend prevention controls, process improvements, or design changes to reduce likelihood.',
            description: 'Prevent frequent failures'
        },
        {
            id: 'risk-5',
            category: 'Risk Assessment',
            icon: React.createElement(TestTube2, { size: 16 }),
            title: 'Detection Improvement Analysis',
            prompt: 'Identify items with poor Detection (D ≥ 7). Recommend specific detection methods: SPC, automated inspection, error-proofing, or 100% testing.',
            description: 'Improve detection capability'
        },
        {
            id: 'risk-6',
            category: 'Risk Assessment',
            icon: React.createElement(Target, { size: 16 }),
            title: 'Risk vs Capability Analysis',
            prompt: 'Compare PFMEA Occurrence ratings against actual process capability (Cpk) data. Flag mismatches where Cpk suggests higher or lower risk than rated.',
            description: 'Validate O ratings with Cpk'
        },
        {
            id: 'risk-7',
            category: 'Risk Assessment',
            icon: React.createElement(TrendingUp, { size: 16 }),
            title: 'Risk Trend Over Iterations',
            prompt: 'Show how total risk has changed across PFMEA revisions. Identify which actions drove the largest risk reductions and what areas still need work.',
            description: 'Track improvement progress'
        },
    ];

    // ========== CONTROL PLANNING (8 prompts) ==========
    const controlPlanningPrompts: PfmeaSuggestedPrompt[] = [
        {
            id: 'cp-1',
            category: 'Control Planning',
            icon: React.createElement(ClipboardList, { size: 16 }),
            title: 'Generate Control Plan Items',
            prompt: 'Based on PFMEA detection controls, generate Control Plan items. For each, specify: process step, characteristic, specification, measurement method, sample size, frequency, and reaction plan.',
            description: 'Create Control Plan entries'
        },
        {
            id: 'cp-2',
            category: 'Control Planning',
            icon: React.createElement(Shield, { size: 16 }),
            title: 'Prevention Control Audit',
            prompt: 'Review all prevention controls in the PFMEA. For each control, assess: Is it implemented? Is it effective? Is it sustainable? Identify gaps and recommend improvements.',
            description: 'Verify prevention controls'
        },
        {
            id: 'cp-3',
            category: 'Control Planning',
            icon: React.createElement(Search, { size: 16 }),
            title: 'Detection Control Audit',
            prompt: 'Review all detection controls. Assess detection capability using actual inspection data. Recommend improvements for controls with poor detection rates.',
            description: 'Verify detection controls'
        },
        {
            id: 'cp-4',
            category: 'Control Planning',
            icon: React.createElement(Wrench, { size: 16 }),
            title: 'Error-Proofing Opportunities',
            prompt: 'Identify opportunities for Poka-Yoke (error-proofing) in the process. For high-risk operations, recommend physical, mechanical, or sensor-based error prevention.',
            description: 'Zero-defect strategies'
        },
        {
            id: 'cp-5',
            category: 'Control Planning',
            icon: React.createElement(BarChart3, { size: 16 }),
            title: 'SPC Implementation Plan',
            prompt: 'For Critical Process Parameters, develop an SPC implementation plan. Specify which control charts to use, sampling strategy, and control limits.',
            description: 'Statistical process control'
        },
        {
            id: 'cp-6',
            category: 'Control Planning',
            icon: React.createElement(TestTube2, { size: 16 }),
            title: 'Sample Size Optimization',
            prompt: 'Review Control Plan sample sizes. For each characteristic, recommend optimal sample sizes based on severity, historical Cpk, and production volume.',
            description: 'Right-size inspection'
        },
        {
            id: 'cp-7',
            category: 'Control Planning',
            icon: React.createElement(AlertTriangle, { size: 16 }),
            title: 'Reaction Plan Review',
            prompt: 'Audit Control Plan reaction plans. Ensure each out-of-control condition has a defined response: Stop? Contain? Notify? Escalate? Document?',
            description: 'Verify response procedures'
        },
        {
            id: 'cp-8',
            category: 'Control Planning',
            icon: React.createElement(FileText, { size: 16 }),
            title: 'PFMEA to Control Plan Trace',
            prompt: 'Generate a traceability matrix from PFMEA failure modes to Control Plan characteristics. Identify any PFMEA risks not covered by the Control Plan.',
            description: 'Verify traceability'
        },
    ];

    // ========== 4M ANALYSIS (6 prompts) ==========
    const fourMPrompts: PfmeaSuggestedPrompt[] = [
        {
            id: '4m-1',
            category: '4M Analysis',
            icon: React.createElement(Users, { size: 16 }),
            title: 'Man (Operator) Analysis',
            prompt: 'List all failure causes related to "Man" category. Assess training adequacy, skill requirements, fatigue risk, and cognitive load. Recommend improvements.',
            description: 'Human factors analysis'
        },
        {
            id: '4m-2',
            category: '4M Analysis',
            icon: React.createElement(Settings, { size: 16 }),
            title: 'Machine (Equipment) Analysis',
            prompt: 'List all failure causes related to "Machine" category. Review equipment capability, maintenance schedules, calibration status, and tool change procedures.',
            description: 'Equipment reliability'
        },
        {
            id: '4m-3',
            category: '4M Analysis',
            icon: React.createElement(Package, { size: 16 }),
            title: 'Material Analysis',
            prompt: 'List all failure causes related to "Material" category. Assess incoming quality controls, supplier capability, storage conditions, and material handling.',
            description: 'Material quality risks'
        },
        {
            id: '4m-4',
            category: '4M Analysis',
            icon: React.createElement(Workflow, { size: 16 }),
            title: 'Method (Procedure) Analysis',
            prompt: 'List all failure causes related to "Method" category. Review work instructions clarity, procedure compliance, training documentation, and standardization.',
            description: 'Procedural risks'
        },
        {
            id: '4m-5',
            category: '4M Analysis',
            icon: React.createElement(BarChart3, { size: 16 }),
            title: '4M Distribution Summary',
            prompt: 'Show the distribution of failure causes across all 4M categories. Identify which category has the highest concentration of risks and recommend focus areas.',
            description: 'Cause category breakdown'
        },
        {
            id: '4m-6',
            category: '4M Analysis',
            icon: React.createElement(TrendingUp, { size: 16 }),
            title: '4M Improvement Tracking',
            prompt: 'Track improvements made in each 4M category over time. Show which interventions (training, equipment, materials, procedures) drove the most risk reduction.',
            description: 'Improvement by category'
        },
    ];

    // ========== AIAG-VDA COMPLIANCE (7 prompts) ==========
    const compliancePrompts: PfmeaSuggestedPrompt[] = [
        {
            id: 'vda-1',
            category: 'AIAG-VDA Compliance',
            icon: React.createElement(Shield, { size: 16 }),
            title: '7-Step Process Audit',
            prompt: 'Audit the PFMEA against AIAG-VDA 7-Step methodology. Check: 1-Planning, 2-Structure Analysis, 3-Function Analysis, 4-Failure Analysis, 5-Risk Analysis, 6-Optimization, 7-Documentation.',
            description: 'Full methodology compliance'
        },
        {
            id: 'vda-2',
            category: 'AIAG-VDA Compliance',
            icon: React.createElement(Zap, { size: 16 }),
            title: 'Action Priority Table Validation',
            prompt: 'Verify all S-O-D combinations map correctly to Action Priority levels (H/M/L) per AIAG-VDA 2019 AP table. Flag any inconsistencies.',
            description: 'Validate AP logic'
        },
        {
            id: 'vda-3',
            category: 'AIAG-VDA Compliance',
            icon: React.createElement(Target, { size: 16 }),
            title: 'Special Characteristics Review',
            prompt: 'Identify all Special Characteristics (CC/SC) in the process. Verify each is marked in PFMEA and has corresponding Control Plan coverage.',
            description: 'SC/CC compliance'
        },
        {
            id: 'vda-4',
            category: 'AIAG-VDA Compliance',
            icon: React.createElement(CheckCircle, { size: 16 }),
            title: 'PPAP Readiness Check',
            prompt: 'Assess PFMEA readiness for PPAP submission. Verify all required fields are complete, cross-functional team is documented, and customer requirements are addressed.',
            description: 'Customer submission ready'
        },
        {
            id: 'vda-5',
            category: 'AIAG-VDA Compliance',
            icon: React.createElement(FileText, { size: 16 }),
            title: 'IATF 16949 Alignment',
            prompt: 'Check PFMEA alignment with IATF 16949 requirements. Verify risk-based thinking integration, documented controls, and continual improvement evidence.',
            description: 'IATF standard compliance'
        },
        {
            id: 'vda-6',
            category: 'AIAG-VDA Compliance',
            icon: React.createElement(Activity, { size: 16 }),
            title: 'Optimization Status Report',
            prompt: 'For all items requiring optimization (High AP), show: Responsible person, Target date, Action status, and Results. Flag overdue items.',
            description: 'Track required actions'
        },
        {
            id: 'vda-7',
            category: 'AIAG-VDA Compliance',
            icon: React.createElement(RefreshCw, { size: 16 }),
            title: 'Living Document Check',
            prompt: 'Verify PFMEA is being maintained as a "living document". Check: Last update date, Revision history, Change control, and Lesson learned integration.',
            description: 'Document maintenance'
        },
    ];

    return [
        ...processAnalysisPrompts,
        ...failureModePrompts,
        ...riskAssessmentPrompts,
        ...controlPlanningPrompts,
        ...fourMPrompts,
        ...compliancePrompts,
    ];
};
